import {
  Tabs as ChakraTabs,
  TabList,
  Tab,
  useTheme,
  Box,
  Alert,
  AlertIcon,
  HStack,
} from '@chakra-ui/react';
import { css as langCss } from '@codemirror/lang-css';
import { html as langHtml } from '@codemirror/lang-html';
import { javascript as langJavascript } from '@codemirror/lang-javascript';
import { sass as langSass } from '@codemirror/lang-sass';
import { indentUnit } from '@codemirror/language';
import { EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { abbreviationTracker } from '@emmetio/codemirror6-plugin';
import { githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import beautify from 'js-beautify';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { MerlinDiffInfo } from 'types/edgeFunctions';

import { createCssAutocomplete, createJsAutocomplete } from './completions';
import { syntaxErrorLinter } from './syntaxLinter';
import { useDevSandbox } from '../DevSandboxProvider';
import SettingsButton from '../SettingsButton';
import { CODE_FREE_LIMIT, CODE_PAID_LIMIT } from '../constants';
import { TCodeTabName } from '../types';
import useSandboxTheme from '../useSandboxTheme';
import { checkCodeLimit } from '../utils';

export const formatCode = (code: string, tabIndex: number, tabSize = 2) => {
  const options = {
    indent_size: tabSize,
    space_in_empty_paren: false,
  };

  switch (tabIndex) {
    case 0:
      return beautify.html(code, options);
    case 1:
      return beautify.css(code, options);
    case 2:
      return beautify.js(code, options);
    default:
      return code;
  }
};

// Map tab display names to merlin diff tab keys
const TAB_TO_DIFF_KEY: Record<string, string> = {
  HTML: 'html',
  CSS: 'css',
  SCSS: 'css',
  PostCSS: 'css',
  'CSS Modules': 'css',
  JS: 'js',
  JSX: 'js',
  TS: 'js',
  VUE: 'js',
};

interface ICodeEditorTabsProps {
  tabIndex: number;
  onChange: (index: number) => void;
  tabs: string[];
}

const GeneratingIndicator = () => {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev % 3) + 1);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <HStack spacing="6px" px={2} flexShrink={0}>
      <Box
        w="14px"
        h="14px"
        borderRadius="full"
        border="2px solid"
        borderColor="purple.400"
        borderTopColor="transparent"
        animation="spin 0.7s linear infinite"
      />
      <Box
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: 'rgba(159, 122, 234, 0.9)',
          fontFamily: 'monospace',
          minWidth: '108px',
        }}
      >
        Generating code{'.'.repeat(dotCount)}
      </Box>
    </HStack>
  );
};

const useMerlinStreamingState = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [diffs, setDiffs] = useState<MerlinDiffInfo[]>([]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setIsStreaming(detail.isStreaming);
      if (detail.diffs && detail.diffs.length > 0) {
        setDiffs(detail.diffs);
      }
      if (!detail.isStreaming) {
        setDiffs([]);
      }
    };

    window.addEventListener('merlin-streaming', handler);
    return () => window.removeEventListener('merlin-streaming', handler);
  }, []);

  return { isStreaming, diffs };
};

const CodeEditorTabs = ({ tabIndex, onChange, tabs }: ICodeEditorTabsProps) => {
  const { opacity } = useTheme();
  const { isStreaming, diffs } = useMerlinStreamingState();
  const { colors } = useSandboxTheme();

  const getDiffForTab = useCallback(
    (tabName: string) => {
      const key = TAB_TO_DIFF_KEY[tabName];
      return key ? diffs.find((d) => d.tab === key) : undefined;
    },
    [diffs]
  );

  return (
    <HStack spacing={0} alignItems="center">
      <ChakraTabs index={tabIndex} onChange={(index) => onChange(index)}>
        <TabList borderBottom="none !important">
          {tabs.map((tab) => {
            const diff = getDiffForTab(tab);
            const hasCodeUpdate = isStreaming && diff;

            return (
              <Tab
                key={tab}
                px={8}
                height="42px"
                borderBottom="3px solid"
                borderColor={hasCodeUpdate ? 'purple.400' : 'transparent'}
                _selected={{
                  backgroundColor: colors.selectedBg,
                  borderBottom: '3px solid',
                  borderBottomColor: hasCodeUpdate ? 'purple.400' : 'purple.200',
                }}
                _hover={{
                  backgroundColor: colors.hoverBg,
                  borderBottom: '3px solid',
                  borderBottomColor: hasCodeUpdate ? 'purple.400' : 'purple.200',
                }}
              >
                <HStack spacing={1.5}>
                  <Text
                    _hover={{ opacity: tabIndex === 0 ? 1 : opacity.primary }}
                    opacity="default"
                  >
                    {tab}
                  </Text>
                  {hasCodeUpdate && (
                    <>
                      {diff.added > 0 && (
                        <Box style={{ fontSize: '10px', fontWeight: 600, color: '#68D391' }}>
                          +{diff.added}
                        </Box>
                      )}
                      {diff.removed > 0 && (
                        <Box style={{ fontSize: '10px', fontWeight: 600, color: '#FC8181' }}>
                          -{diff.removed}
                        </Box>
                      )}
                    </>
                  )}
                </HStack>
              </Tab>
            );
          })}
        </TabList>
      </ChakraTabs>
      {isStreaming && diffs.length > 0 && <GeneratingIndicator />}
    </HStack>
  );
};

interface ICodeAlertProps {
  maxChars: number;
  currentChars: number;
  codeTabName: TCodeTabName;
}

const CodeAlert = ({ maxChars, currentChars, codeTabName }: ICodeAlertProps) => {
  return (
    <Alert
      status="warning"
      position="absolute"
      left={0}
      top={0}
      zIndex={9999999}
      width="100%"
      backgroundColor="yellow.700"
    >
      <AlertIcon />
      Exceeded characters limit ({currentChars}/{maxChars}) for {codeTabName}.
    </Alert>
  );
};

interface ICodeEditorProps {
  isAccordionOpen?: boolean | null;
}

const CodeEditor = ({ isAccordionOpen = false }: ICodeEditorProps) => {
  const htmlEditorRef = useRef<any>(null);
  const cssEditorRef = useRef<any>(null);
  const jsEditorRef = useRef<any>(null);

  const { isSmallScreen } = useMediaQueries();

  const { isFree } = useCurrentUser();

  const {
    codeTabs: { html, css, js, tabNames, tabIndex },
    handlers: {
      onCodeTabIndexChange,
      onHtmlChange,
      onCssChange,
      onJsChange,
      onEditorSettingsChange,
      onEditorSettingsReset,
    },
    editorSettings,
    footer: { hideTools: isReadOnlyView },
  } = useDevSandbox();

  const codeTabs = tabNames || [];
  const index = tabIndex || 0;

  const isHTMLTab = codeTabs[index] === 'HTML';
  // CSS tab can have different labels based on settings
  const isCSSTab = index === 1;
  // JS tab can have different labels based on settings
  const isJsTab = index === 2;

  const { resolved: resolvedTheme, colors } = useSandboxTheme();

  // Build CodeMirror extensions based on editor settings
  const editorFontSize = editorSettings.fontSize;
  const editorTheme = resolvedTheme === 'light' ? githubLight : 'dark';
  const showLineNumbers = editorSettings.lineNumbers;
  const enableWordWrap = editorSettings.wordWrap;
  const tabSizeValue = editorSettings.tabSize;

  // Custom theme extension for font size
  const fontSizeTheme = useMemo(
    () =>
      EditorView.theme({
        '&': {
          fontSize: `${editorFontSize}px`,
        },
        '.cm-gutters': {
          fontSize: `${editorFontSize}px`,
        },
      }),
    [editorFontSize]
  );

  // Syntax error linter (Lezer parse errors → inline diagnostics)
  const linterExtension = useMemo(() => syntaxErrorLinter(), []);

  // Build common extensions array
  const getCommonExtensions = useMemo(() => {
    const extensions = [
      fontSizeTheme,
      EditorState.tabSize.of(tabSizeValue),
      indentUnit.of(' '.repeat(tabSizeValue)),
      linterExtension,
    ];
    if (enableWordWrap) {
      extensions.push(EditorView.lineWrapping);
    }
    return extensions;
  }, [fontSizeTheme, tabSizeValue, enableWordWrap, linterExtension]);

  // Autocomplete extensions based on framework
  const jsAutocomplete = useMemo(
    () => createJsAutocomplete(editorSettings.framework),
    [editorSettings.framework]
  );
  const cssAutocomplete = useMemo(() => createCssAutocomplete(), []);

  // Keyboard shortcuts (disabled in read-only solution views)
  useEffect(() => {
    if (isReadOnlyView) return;

    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;

      // Cmd/Ctrl+Shift+F: Format current tab's code
      if (isMod && e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        e.stopPropagation();
        const currentCode = isHTMLTab ? html : isCSSTab ? css : js;
        const formatted = formatCode(currentCode ?? '', index, editorSettings.tabSize);
        const setter = isHTMLTab ? onHtmlChange : isCSSTab ? onCssChange : onJsChange;
        setter(formatted);
      }

      // Cmd/Ctrl+Shift+S: Save shortcut (format first if autoFormat enabled)
      if (isMod && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopPropagation();
        if (editorSettings.autoFormat) {
          if (html) onHtmlChange(formatCode(html, 0, editorSettings.tabSize));
          if (css) onCssChange(formatCode(css, 1, editorSettings.tabSize));
          if (js) onJsChange(formatCode(js, 2, editorSettings.tabSize));
        }
        window.dispatchEvent(new CustomEvent('bds:save-shortcut'));
      }

      // Cmd/Ctrl+Shift+Enter: Run tests (for practice problems)
      if (isMod && e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('bds:run-tests'));
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [html, css, js, index, isHTMLTab, isCSSTab, isJsTab, onHtmlChange, onCssChange, onJsChange, isReadOnlyView, editorSettings.autoFormat, editorSettings.tabSize]);

  return (
    <Box width="100%" height={isAccordionOpen ? '56%' : '100%'}>
      <HStack width="100%" justifyContent="space-between" spacing={0} overflowX="auto">
        <Box height="42px">
          <CodeEditorTabs
            tabs={codeTabs}
            tabIndex={index}
            onChange={(index) => onCodeTabIndexChange(index)}
          />
        </Box>
        {!isSmallScreen && (
          <HStack spacing={2} mr={2}>
            {/* Editor Settings Button */}
            <SettingsButton
              settings={editorSettings}
              onSettingsChange={onEditorSettingsChange}
              onReset={onEditorSettingsReset}
            />
          </HStack>
        )}
      </HStack>
      <Box height="2px" backgroundColor={colors.editorBg} />
      <Box height="calc(100% - 42px)" backgroundColor={colors.editorBg} position="relative">
        {html && !checkCodeLimit(isFree, html) && isHTMLTab && (
          <CodeAlert
            currentChars={html.length}
            maxChars={isFree ? CODE_FREE_LIMIT : CODE_PAID_LIMIT}
            codeTabName="HTML"
          />
        )}
        {css && !checkCodeLimit(isFree, css) && isCSSTab && (
          <CodeAlert
            currentChars={css.length}
            maxChars={isFree ? CODE_FREE_LIMIT : CODE_PAID_LIMIT}
            codeTabName="CSS"
          />
        )}
        {js && !checkCodeLimit(isFree, js) && isJsTab && (
          <CodeAlert
            currentChars={js.length}
            maxChars={isFree ? CODE_FREE_LIMIT : CODE_PAID_LIMIT}
            codeTabName="JS"
          />
        )}
        <CodeMirror
          value={html}
          ref={htmlEditorRef}
          id="bds-code-editor-html"
          extensions={[
            langHtml({ selfClosingTags: true, autoCloseTags: false }),
            abbreviationTracker(),
            EditorState.readOnly.of(false),
            ...getCommonExtensions,
          ]}
          theme={editorTheme}
          style={{
            display: isHTMLTab ? 'initial' : 'none',
          }}
          basicSetup={{ foldGutter: false, lineNumbers: showLineNumbers }}
          onChange={isHTMLTab ? onHtmlChange : undefined}
        />
        <CodeMirror
          value={css}
          ref={cssEditorRef}
          id="bds-code-editor-css"
          extensions={[editorSettings.cssMode === 'scss' ? langSass() : langCss(), cssAutocomplete, ...getCommonExtensions]}
          theme={editorTheme}
          basicSetup={{ foldGutter: false, lineNumbers: showLineNumbers }}
          style={{
            display: isCSSTab ? 'initial' : 'none',
          }}
          onChange={isCSSTab ? onCssChange : undefined}
        />
        <CodeMirror
          value={js}
          ref={jsEditorRef}
          id="bds-code-editor-js"
          extensions={[
            langJavascript({ jsx: true, typescript: true }),
            jsAutocomplete,
            ...getCommonExtensions,
          ]}
          theme={editorTheme}
          style={{
            display: isJsTab ? 'initial' : 'none',
          }}
          basicSetup={{ foldGutter: false, lineNumbers: showLineNumbers }}
          onChange={isJsTab ? onJsChange : undefined}
        />
      </Box>
    </Box>
  );
};

export default CodeEditor;
